import { Trans } from '@lingui/react/macro';
import { CheckCircle2, FileEdit, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { TemplateStatus } from '@/types/saved-template';

/**
 * Coloured status pill for a `SavedTemplate` (parity WW4). Maps each
 * template status to the matching EDS `<Badge />` variant plus a small
 * Lucide icon, mirroring v1's icon-and-label style.
 *
 * When the host opts into the `rotaready` variant, the badge is restyled as a
 * compact chip using the host's semantic colour tokens so it matches the
 * standard Rotaready status treatment.
 */
export function StatusBadge({ status, icon, label, size = 'sm' }: StatusBadgeProps) {
  const meta = STATUS_META[status];
  return (
    <Badge
      variant={meta.variant}
      className={cn(
        'rotaready:min-w-[60px] rotaready:rounded-[3px] rotaready:px-2.5 rotaready:py-0 rotaready:font-semibold rotaready:justify-center rotaready:gap-0',
        size === 'sm' && 'rotaready:h-[30px] rotaready:text-sm rotaready:leading-[30px]',
        size === 'xs' && 'rotaready:h-6 rotaready:text-xs rotaready:leading-6',
        status === 'published' && 'rotaready:bg-[#e5f6ee] rotaready:text-[#00ab5b]',
        status === 'draft' && 'rotaready:bg-[#fff9e6] rotaready:text-[#ffc400]',
        status === 'deleted' && 'rotaready:bg-[#ffe8e6] rotaready:text-[#ff1e0e]',
      )}
    >
      <span className="rotaready:hidden">{icon ?? meta.icon}</span>
      {label ?? meta.label}
    </Badge>
  );
}

/**
 * Single source of truth for the badge's variant, icon and label per `TemplateStatus`.
 */
const STATUS_META: Record<TemplateStatus, StatusMeta> = {
  published: { variant: 'success', icon: <CheckCircle2 />, label: <Trans>Published</Trans> },
  draft: { variant: 'warning', icon: <FileEdit />, label: <Trans>Draft</Trans> },
  deleted: { variant: 'destructive', icon: <Trash2 />, label: <Trans>Deleted</Trans> },
};

interface StatusMeta {
  variant: 'success' | 'warning' | 'destructive';
  icon: ReactNode;
  label: ReactNode;
}

interface StatusBadgeProps {
  status: TemplateStatus;
  icon?: ReactNode;
  label?: ReactNode;
  size?: 'sm' | 'xs';
}
